import type { Account } from '../types/account.js';

// Multi-share Campaign visibility resolution (Pre-P2b-3 §5).
//
// A Campaign can be shared with one or more Accounts (brand / group /
// location). Visibility resolves via inclusive walk-up: a Campaign is
// visible to a location iff any shareTarget appears in the location's
// ancestor chain (including the location itself).
//
// One primitive — isAncestorOrSelf — powers two domain helpers:
//
//   - isCampaignVisibleToLocation(campaign, locationId, byId)
//     Used wherever we filter Campaigns for a specific location: per-row
//     Campaign dropdowns on the master grid, the future Account-scoped
//     Campaign list. "ancestor" walks up from the location.
//
//   - isCampaignVisibleInBrandContext(campaign, brandId, byId)
//     Used for brand-scoped Campaign lists. The brand-list semantics
//     (spec §5.6): show Campaigns where any shareTarget walks up to or
//     is the brand. Equivalently: brand is an ancestor-or-self of any
//     shareTarget — same primitive, swapped arguments.
//
// Both helpers fail safe for old-shape Campaign docs (missing or empty
// shareTargets → invisible everywhere). Brief: "if a stale Campaign doc
// exists with the old shape, treat shareTargets as effectively empty —
// Campaign is invisible from all dropdowns. Fail safe, not loud." The
// Campaign is still readable by ID for admin inspection — we only gate
// the visibility queries.

// Structural type so the helpers don't import the full Campaign shape.
// Lets the visibility module ship before the Campaign type swap, and
// keeps the dependency surface minimal for tests.
export type CampaignWithShareTargets = {
  shareTargets?: Array<{ accountId: string }>;
};

// Subset of Account needed for the walk-up. Matches the shape MasterGrid
// already keeps in its `accountsById` map (Map<string, Account>).
export type AccountForVisibility = Pick<Account, 'id' | 'parentAccountId'>;

// Primitive: walking up from descendantId via parentAccountId, do we ever
// reach candidateId? (descendantId itself counts as the start of the walk.)
//
// Returns false on any of:
//   - descendantId not present in byId (caller error or stale data)
//   - cycle detected (defensive — should never happen in production data)
//   - walk reaches a null parent without ever hitting candidateId
//
// O(depth-of-tree). Account hierarchies in this product are shallow
// (brand → group → location, ~3 levels), so even a brute-force walk is
// fast. The visited-set guard is defence-in-depth against bad data.
export function isAncestorOrSelf(
  candidateId: string,
  descendantId: string,
  byId: Map<string, AccountForVisibility>,
): boolean {
  if (candidateId === descendantId) return true;
  let cursor: string | null = descendantId;
  const visited = new Set<string>();
  while (cursor !== null) {
    if (visited.has(cursor)) return false; // cycle guard
    visited.add(cursor);
    const acc = byId.get(cursor);
    if (!acc) return false;
    if (acc.parentAccountId === candidateId) return true;
    cursor = acc.parentAccountId;
  }
  return false;
}

// Per-row dropdown / location-scoped list filtering.
export function isCampaignVisibleToLocation(
  campaign: CampaignWithShareTargets,
  locationId: string,
  byId: Map<string, AccountForVisibility>,
): boolean {
  const targets = campaign.shareTargets ?? [];
  if (targets.length === 0) return false; // old-shape / not-yet-shared
  for (const t of targets) {
    if (isAncestorOrSelf(t.accountId, locationId, byId)) return true;
  }
  return false;
}

// Brand-scoped list filtering. Returns true iff any shareTarget is the
// brand or any descendant of the brand.
export function isCampaignVisibleInBrandContext(
  campaign: CampaignWithShareTargets,
  brandId: string,
  byId: Map<string, AccountForVisibility>,
): boolean {
  const targets = campaign.shareTargets ?? [];
  if (targets.length === 0) return false;
  for (const t of targets) {
    if (isAncestorOrSelf(brandId, t.accountId, byId)) return true;
  }
  return false;
}

// Pre-P2b-3 polish (Fix 3): "Campaigns directly shared with THIS
// account" — strict equality, no walk-up, no walk-down. Used by the
// non-brand AccountDetail Campaigns section so a group / location
// page surfaces only Campaigns whose shareTargets list includes that
// exact accountId. A Campaign shared with the brand is NOT shown on
// a location page under it via this filter — that's the
// isCampaignVisibleToLocation surface; this one is for "shared with
// me directly".
//
// Cheap pure check; no Account hierarchy needed.
export function isCampaignDirectlySharedWith(
  campaign: CampaignWithShareTargets,
  accountId: string,
): boolean {
  const targets = campaign.shareTargets ?? [];
  return targets.some((t) => t.accountId === accountId);
}
